/**
 * Factory to create initial products to start an offer flow.
 */
export default function useProductFactory() {
    /**
     * Create a normal product (for normal offer flow)
     */
    function createProduct(
        productKey: ProductKey,
        productId: ProductId,
        productConfigPreset?: ProductConfigPreset,
        productPreset?: ProductPreset,
    ): Product {
        const baseConfig = productConfigs.find(config => config.key === productKey);
        const productConfig = { ...baseConfig, ...productConfigPreset } as ProductConfig;

        switch (productConfig?.type) {
            case 'Activity':
                return createProductActivity(productConfig, productId, productPreset as ProductPresetActivity);
            case 'P2P':
                return createProductP2P(productConfig, productId, productPreset as ProductPresetP2P);
            case 'Accommodation':
                return createProductAccommodation(productConfig, productId);
            default:
                throw new Error('Product Config not found');
        }
    }

    /**
     * Create upsell for a product.
     * Product is related to a parent product.
     */
    function createUpsell(
        productKey: ProductKey,
        productId: ProductId,
        upsellParent: Product,
        productConfigPreset?: ProductConfigPreset,
    ): Product {
        const baseConfig = productConfigs.find(config => config.key === productKey);
        const productConfig = { ...baseConfig, ...productConfigPreset } as ProductConfig;

        switch (productConfig?.type) {
            case 'Activity':
                return createUpsellActivity(productConfig, productId, upsellParent);
            case 'P2P':
                return createUpsellP2P(productConfig, productId, upsellParent);
            case 'Accommodation':
                return createUpsellAccommodation(productConfig, productId, upsellParent);
            default:
                throw new Error('Product Config not found');
        }
    }

    /**
     * Create activity upsell product
     */
    function createUpsellActivity(productConfig: ProductConfigActivity, productId: ProductId, upsellParent: Product): ProductActivity {
        const product = _createProductActivity(productConfig, productId, upsellParent);

        if (productConfig.isRental && upsellParent.config.type === 'Activity') {
            const parentActivity = upsellParent as ProductActivity;
            product.dateFrom = parentActivity.dateFrom;
            product.dateUntil = parentActivity.dateUntil;
        }

        return product;
    }

    /**
     * Create activity product
     */
    function createProductActivity(productConfig: ProductConfigActivity, productId: ProductId, productPreset?: ProductPresetActivity): ProductActivity {
        return _createProductActivity(productConfig, productId, undefined, productPreset);
    }

    /**
     * Create activity product (internal)
     */
    function _createProductActivity(productConfig: ProductConfigActivity, productId: ProductId, upsellParent?: Product, productPreset?: ProductPresetActivity): ProductActivity {
        const isUpsell = !!upsellParent;
        return {
            id: productId,
            activityId: productConfig.activityId ?? '',
            listingId: productConfig.listingId,
            config: productConfig,
            content: {} as OfferFlowContent,
            isUpsell,
            upsellParent,
            passengerRefs: [],
            dateFrom: productPreset?.dateFrom ?? undefined,
            dateUntil: productPreset?.dateUntil ?? undefined,
            time: undefined,
            startingPoint: undefined,
            option: undefined,
            rentalEquipment: [],
            offer: undefined,
            startingPrice: productPreset?.startingPrice,
            reductionCardTypes: [],
            fulfillmentOptions: [],
            requiredPassengerInformation: undefined,
            requiredPurchaserInformation: undefined,
            status: {
                isTouched: true,
                contentLocale: undefined,
                listingLocale: undefined,
            },
        } satisfies ProductActivity;
    }

    /**
     * Create P2P upsell product
     */
    function createUpsellP2P(productConfig: ProductConfigP2P, productId: ProductId, upsellParent: Product): ProductP2P {
        return _createProductP2P(productConfig, productId, upsellParent);
    }

    /**
     * Create P2P product
     */
    function createProductP2P(productConfig: ProductConfigP2P, productId: ProductId, productPreset?: ProductPresetP2P): ProductP2P {
        return _createProductP2P(productConfig, productId, undefined, productPreset);
    }

    /**
     * Create P2P product (internal)
     */
    function _createProductP2P(productConfig: ProductConfigP2P, productId: ProductId, upsellParent?: Product, productPreset?: ProductPresetP2P): ProductP2P {
        const isUpsell = !!upsellParent;
        return {
            id: productId,
            listingId: productConfig.listingId,
            config: productConfig,
            content: {} as OfferFlowContent,
            isUpsell,
            upsellParent,
            passengerRefs: [],
            dateFrom: productPreset?.dateFrom,
            origin: productPreset?.origin ?? productConfig.origins?.[0],
            destination: productConfig.destinations?.[0],
            returnOrigin: productConfig.returnOrigins?.[0] ?? productConfig.destinations?.[0],
            returnDestination: productPreset?.origin ?? productConfig.returnDestinations?.[0] ?? productConfig.origins?.[0],
            numberOfPeople: productConfig.defaultNumberOfPeople ?? 1,
            isRoundtrip: productConfig.isRoundtrip === true,
            isDirectionSwitched: false,
            isEPRDisabled: false,
            routeVariantUp: productPreset?.routeVariantUp ?? productConfig.routeVariantUp,
            routeVariantDown: productPreset?.routeVariantDown ?? productConfig.routeVariantDown,
            stayDuration: undefined,
            tripUp: undefined,
            tripDown: undefined,
            offer: undefined,
            startingPrice: productPreset?.startingPrice,
            reductionCardTypes: [],
            fulfillmentOptions: [],
            requiredPassengerInformation: undefined,
            requiredPurchaserInformation: undefined,
            status: {
                isTouched: true,
                contentLocale: undefined,
                listingLocale: undefined,
            },
            presets: {
                tripUpId: productPreset?.tripUpId,
                tripDownId: productPreset?.tripDownId,
            },
        } satisfies ProductP2P;
    }

    /**
     * Create accommodation upsell product
     */
    function createUpsellAccommodation(productConfig: ProductConfigAccommodation, productId: ProductId, upsellParent: Product): ProductAccommodation {
        return _createProductAccommodation(productConfig, productId, upsellParent);
    }

    /**
     * Create accommodation product
     */
    function createProductAccommodation(productConfig: ProductConfigAccommodation, productId: ProductId): ProductAccommodation {
        return _createProductAccommodation(productConfig, productId);
    }

    /**
     * Create accommodation product (internal)
     */
    function _createProductAccommodation(productConfig: ProductConfigAccommodation, productId: ProductId, upsellParent?: Product): ProductAccommodation {
        const isUpsell = !!upsellParent;
        return {
            id: productId,
            listingId: undefined,
            config: productConfig,
            content: {} as OfferFlowContent,
            isUpsell,
            upsellParent,
            passengerRefs: [],
            dateFrom: undefined,
            dateUntil: undefined,
            rooms: [],
            remark: '',
            offer: undefined,
            reductionCardTypes: [],
            fulfillmentOptions: [],
            requiredPassengerInformation: undefined,
            requiredPurchaserInformation: undefined,
            status: {
                isTouched: true,
                contentLocale: undefined,
                listingLocale: undefined,
            },
        } satisfies ProductAccommodation;
    }

    return {
        createProduct,
        createProductActivity,
        createProductP2P,

        createUpsell,
        createUpsellActivity,
        createUpsellP2P,
        createUpsellAccommodation,
    };
}
